import { resolveWidgetLanguage } from "./resolve-widget-language";

// Scoped per shop: the office opens the widget for several different shops in
// one browser tab (demo button, admin "Créer un retour Baback"), and a single
// flat key would let a pick made for one shop outrank every other shop's own
// default language for the rest of the tab's session (BBS-430).
function manualLanguageStorageKey(shopId: string): string {
  return `baback:widget-language:${shopId}`;
}

export function readManualLanguage(shopId: string | undefined): string | null {
  return shopId ? sessionStorage.getItem(manualLanguageStorageKey(shopId)) : null;
}

export function writeManualLanguage(shopId: string, code: string): void {
  sessionStorage.setItem(manualLanguageStorageKey(shopId), code);
}

type WidgetLocation = Pick<Location, "hash" | "search">;

// The widget runs under a HashRouter, so its query normally lives inside the
// hash. A hand-written URL puts it before the hash instead, which is the shape
// a merchant testing a locale reaches for first.
function readQueryParam(location: WidgetLocation, name: string): string | null {
  const hashQuery = location.hash.split("?")[1] ?? "";
  return (
    new URLSearchParams(hashQuery).get(name) ??
    new URLSearchParams(location.search).get(name)
  );
}

// Available before react-router matches anything: the route is always
// `#/return-widget/:providerId`, and the shop id has to be known this early to
// scope the manual-language lookup below.
export function shopIdFromHash(location: WidgetLocation = window.location): string | undefined {
  return location.hash.match(/^#\/return-widget\/([^/?#]+)/)?.[1];
}

/**
 * Language known before anything is fetched: the customer's own pick, `?lang=`,
 * then the storefront locale forwarded by the embed script. i18next starts on
 * it, so the first bundle downloaded is already the right one. The shop's
 * language list is unknown this early: `useWidgetLanguage` bounds the result to
 * it once the shop is loaded.
 */
export function readBootLanguage(
  location: WidgetLocation = window.location,
): string | undefined {
  return resolveWidgetLanguage(
    [
      { source: "manual", code: readManualLanguage(shopIdFromHash(location)) },
      { source: "url", code: readQueryParam(location, "lang") },
      { source: "storefront", code: readQueryParam(location, "storefrontLang") },
    ],
    [],
  )?.code;
}
